
import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Lock, 
  Layers, 
  Database, 
  Shield, 
  Settings,
  Save
} from 'lucide-react';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useToast } from "@/hooks/use-toast";

const ConfigSection = ({ 
  icon: Icon, 
  title, 
  children 
}: { 
  icon: React.ElementType; 
  title: string; 
  children: React.ReactNode;
}) => (
  <div className="mb-6 pb-6 border-b border-border last:border-0 last:mb-0 last:pb-0">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-5 w-5 text-primary" />
      <h3 className="text-lg font-semibold">{title}</h3>
    </div>
    <div className="pl-7">
      {children}
    </div>
  </div>
);

const SecurityConfig = () => {
  const { toast } = useToast();

  const handleSaveConfig = () => {
    toast({
      title: "Configuration saved",
      description: "Your security settings have been updated successfully.",
    });
  };

  const handleResetConfig = () => {
    toast({
      title: "Configuration reset",
      description: "All settings have been reset to default values.",
      variant: "destructive",
    });
  };

  return (
    <div className="p-4 border rounded-lg">
      <h2 className="text-lg font-bold mb-6">Security Configuration</h2>
      
      <ConfigSection icon={Database} title="Compression Settings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Compression Algorithm</label>
            <Select defaultValue="zstd">
              <SelectTrigger>
                <SelectValue placeholder="Select algorithm" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zstd">Zstandard (recommended)</SelectItem>
                <SelectItem value="lz4">LZ4 (faster)</SelectItem>
                <SelectItem value="gzip">GZIP (compatibility)</SelectItem>
                <SelectItem value="brotli">Brotli (highest ratio)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Compression Level</label>
            <Select defaultValue="balanced">
              <SelectTrigger>
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minimal">Minimal (fastest)</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="maximum">Maximum (slowest)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfigSection>
      
      <ConfigSection icon={Layers} title="Data Shredding">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Minimum Fragments</label>
            <Input type="number" defaultValue="3" min="2" max="10" />
            <p className="text-xs text-muted-foreground">Minimum fragments needed to restore data</p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Total Fragments</label>
            <Input type="number" defaultValue="5" min="3" max="20" />
            <p className="text-xs text-muted-foreground">Total number of fragments created</p>
          </div>
          
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium">Geographic Distribution</label>
            <Select defaultValue="global">
              <SelectTrigger>
                <SelectValue placeholder="Select distribution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single Region</SelectItem>
                <SelectItem value="dual">Dual Region</SelectItem>
                <SelectItem value="global">Global Distribution</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfigSection>
      
      <ConfigSection icon={Lock} title="Encryption Settings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Encryption Algorithm</label>
            <Select defaultValue="ml-kem">
              <SelectTrigger>
                <SelectValue placeholder="Select algorithm" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aes-128">AES 128</SelectItem>
                <SelectItem value="aes-256">AES 256</SelectItem>
                <SelectItem value="ml-kem">ML-KEM (Crystals-Kyber) (recommended)</SelectItem>
                <SelectItem value="ml-dsa">ML-DSA (Crystals-Dilithium)</SelectItem>
                <SelectItem value="slh-dsa">SLH-DSA (SPHINCS+)</SelectItem>
                <SelectItem value="hqc">HQC (ML-KEM Alternate)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Key Rotation</label>
            <Select defaultValue="30">
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Every 7 days</SelectItem>
                <SelectItem value="30">Every 30 days</SelectItem>
                <SelectItem value="90">Every 90 days</SelectItem>
                <SelectItem value="manual">Manual rotation only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfigSection>
      
      <ConfigSection icon={Shield} title="Authentication Requirements">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Required Factors</label>
            <Select defaultValue="2fa">
              <SelectTrigger>
                <SelectValue placeholder="Select authentication level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">Password Only</SelectItem>
                <SelectItem value="2fa">Two-Factor (2FA)</SelectItem>
                <SelectItem value="mfa">Multi-Factor (MFA)</SelectItem>
                <SelectItem value="biometric">Biometric + 2FA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Session Timeout</label>
            <Select defaultValue="15">
              <SelectTrigger>
                <SelectValue placeholder="Select timeout" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 minutes</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfigSection>
      
      <ConfigSection icon={Settings} title="Interface Settings">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Theme Mode</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Toggle between light and dark mode</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </ConfigSection>
      
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          variant="outline" 
          onClick={handleResetConfig}
        >
          Reset to Defaults
        </Button>
        <Button 
          onClick={handleSaveConfig}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default SecurityConfig;
